import { useRouteLoaderData } from "@remix-run/react";
import { motion } from "framer-motion";
import { AlertTriangle, Ticket } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FeaturedBadge,
  ServiceCardSubtitle,
  ServiceInfoButton,
  ServicePriceLabel,
  getCardPrice,
  getServiceCardTexts,
} from "~/components/booking/flow/ServiceCardExtras";
import { keepAnimationOnMainThread } from "~/lib/motion";
import { cn } from "~/lib/utils";
import { sortByWpServiceOrder } from "~/lib/wp-order";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

interface ServiceTypeSelectProps {
  services: any[];
  showCodes?: boolean;
  onSelect: (cmsService: any, service?: any) => void;
  selectedValue: any;
  bookingCart?: any;
  isVoucherBooking?: boolean;
  loading?: boolean;
  isUpselling?: boolean;
  onSkip?: (cmsService: any, apiService?: any) => void;
  groupVariantsByCmsService?: boolean;
  totalGuests?: number;
}

const ServiceTypeSelect = ({
  services,
  onSelect,
  selectedValue,
  bookingCart,
  isVoucherBooking = false,
  loading = false,
  isUpselling = false,
  onSkip,
  groupVariantsByCmsService = false,
  totalGuests,
}: ServiceTypeSelectProps) => {
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const { bookingCart: bookingCartFromStore } = useBookingCartStore();
  const [servicesToShow, setServicesToShow] = useState<any[]>([]);

  const currentBookingCart = bookingCart || bookingCartFromStore;

  const getApplicableVouchersCount = (
    serviceCodes: string[],
    serviceType?: string,
  ) => {
    const vouchers = currentBookingCart?.vouchers || [];
    if (vouchers.length === 0 || serviceCodes.length === 0) return 0;

    // Filtriamo i voucher in base al tipo di servizio mostrato nel card:
    // - Upselling: voucher con OnlyIntegration=true.
    // - Pacchetto (apiService.Type === "Package"): voucher Category=PACKAGE.
    // - Ingresso (gli altri servizi principali, Type=Ticket): voucher
    //   Category=TICKET (o Ticket=true per copertura).
    const vouchersForThisStep = vouchers.filter((v: any) => {
      const vd = v.voucherData;
      if (!vd) return false;
      if (isUpselling) return vd.OnlyIntegration === true;
      if (serviceType === "Package") return vd.Category === "PACKAGE";
      return vd.Category === "TICKET" || vd.Ticket === true;
    });
    if (vouchersForThisStep.length === 0) return 0;

    const hasServiceWithVoucherInCart = currentBookingCart?.BasketItems?.some(
      (item: any) =>
        serviceCodes.includes(item.Service) &&
        (item.VoucherCode || item.GiftCardType),
    );

    if (hasServiceWithVoucherInCart) return 0;

    return vouchersForThisStep.filter((v: any) => {
      if (
        !v.voucherData?.ServicesIncludedCodes ||
        v.voucherData.ServicesIncludedCodes.length === 0
      ) {
        return true;
      }
      return serviceCodes.some((code) =>
        v.voucherData.ServicesIncludedCodes.includes(code),
      );
    }).length;
  };

  useEffect(() => {
    const allMapped: any[] = [];
    services?.forEach((service: any) => {
      if (service.cmsService && service.Code) {
        allMapped.push({
          cmsService: service.cmsService,
          apiService: service,
        });
      } else {
        const cms = (rootData.servicesData ?? []).find((cms: any) =>
          cms.acf?.codes?.some((c: any) => c.code === service.Code),
        );
        if (cms) {
          allMapped.push({ cmsService: cms, apiService: service });
        }
      }
    });

    // Le card "show_anyway" sono ghost (l'API non ritorna nulla) e non
    // partecipano alle metriche di filtraggio package/non-package: vanno
    // sempre mostrate così come sono.
    const hasAnyNonPackage = allMapped.some(
      (s: any) =>
        s.apiService.Type !== "Package" && !s.apiService.isShowAnyway,
    );

    // Filtriamo i Ticket solo se l'utente possiede effettivamente un voucher
    // di categoria PACKAGE: in quel caso ha senso mostrare solo i pacchetti
    // perché il voucher copre un pacchetto. Se il voucher è di altro tipo
    // (Ticket o upselling), mostriamo tutto: i Ticket sono ingressi reali
    // che l'utente vorrà poter scegliere.
    const hasPackageVoucher = !!currentBookingCart?.vouchers?.some(
      (v: any) => v.voucherData?.Category === "PACKAGE",
    );

    const newServicesToShow = allMapped.filter((s: any) => {
      // Le card show_anyway passano sempre i filtri.
      if (s.apiService.isShowAnyway) return true;
      // Nello step upselling i filtri package/ticket NON si applicano: gli
      // upselling sono sempre servizi non-package e vanno mostrati a
      // prescindere dal tipo di voucher. Senza questa guardia, con un voucher
      // di Category=PACKAGE la riga sotto scarterebbe tutti i non-package,
      // svuotando la lista e mostrando "errore nella ricerca dei servizi"
      // anche se gli upselling sono regolarmente arrivati dall'API.
      if (isUpselling) return true;
      const isPkg = s.apiService.Type === "Package";
      // Voucher mode: se il voucher è di tipo Package, mostra solo pacchetti.
      if (isVoucherBooking && hasPackageVoucher && !isPkg) {
        return false;
      }
      // Modalità normale: i pacchetti non compaiono tra le card servizio
      // (restano disponibili come code del loro servizio nello step durata).
      if (!isVoucherBooking && hasAnyNonPackage && isPkg) {
        return false;
      }
      return true;
    });

    const groupedServicesToShow: any[] = (() => {
      if (!groupVariantsByCmsService) {
        return newServicesToShow.map((s: any) => ({
          ...s,
          variants: [s.apiService],
        }));
      }
      const result: any[] = [];
      const cmsIdToIndex = new Map<any, number>();
      newServicesToShow.forEach((s: any) => {
        const isPackage = s.apiService.Type === "Package";
        if (isPackage || !s.cmsService?.id) {
          result.push({ ...s, variants: [s.apiService] });
          return;
        }
        const existingIdx = cmsIdToIndex.get(s.cmsService.id);
        if (existingIdx !== undefined) {
          result[existingIdx].variants.push(s.apiService);
        } else {
          cmsIdToIndex.set(s.cmsService.id, result.length);
          result.push({ ...s, variants: [s.apiService] });
        }
      });
      return result;
    })();

    // Per decidere se sono "tutti pacchetti" ignoriamo le card show_anyway:
    // non sono servizi reali e non devono influenzare l'auto-skip.
    const visibleAreAllPackages =
      groupedServicesToShow.some((s: any) => !s.apiService.isShowAnyway) &&
      groupedServicesToShow.every(
        (s: any) =>
          s.apiService.Type === "Package" || s.apiService.isShowAnyway,
      );

    if (
      visibleAreAllPackages &&
      newServicesToShow.filter((s: any) => s.apiService.Type === "Package")
        .length > 1
    ) {
      const packages = newServicesToShow.filter(
        (s: any) => s.apiService.Type === "Package",
      );

      if (packages.length > 1) {
        const firstStepServices = packages
          .map((pkg: any) => {
            const firstStep = pkg.apiService.PackageComposition?.find(
              (step: any) => step.StepNr === 1,
            );
            return firstStep?.ServicesList?.[0]?.Code;
          })
          .filter(Boolean);

        if (firstStepServices.length === packages.length) {
          const allSameService = firstStepServices.every(
            (code: string) => code === firstStepServices[0],
          );

          if (allSameService && onSkip) {
            onSkip(packages[0].cmsService, packages[0].apiService);
            return;
          }
        }
      }
    }


    if (groupedServicesToShow.length === 1 && onSkip) {
      onSkip(
        groupedServicesToShow[0].cmsService,
        groupedServicesToShow[0].apiService,
      );
    } else {
      // Le card seguono l'ordine impostato su WordPress (menu_order del
      // servizio, poi ordine dei code), non quello dell'API. Si ordinano solo
      // le card: dentro un gruppo le varianti restano nell'ordine dell'API,
      // così l'apiService rappresentativo (il primo) non cambia.
      setServicesToShow(
        sortByWpServiceOrder(
          groupedServicesToShow,
          (s: any) => (s.variants || [s.apiService]).map((v: any) => v?.Code),
          rootData.servicesData,
        ),
      );
    }
  }, [services, rootData, onSkip, isVoucherBooking, groupVariantsByCmsService]);

  // const servicesToShow: any[] = (() => {
  //   const result: any[] = [];
  //   services?.forEach((service: any) => {
  //     if (service.cmsService && service.Code) {
  //       result.push({ cmsService: service.cmsService, apiService: service });
  //     } else {
  //       const cms = (rootData.servicesData ?? []).find((cms: any) =>
  //         cms.acf?.codes?.some((c: any) => c.code === service.Code)
  //       );
  //       if (cms) {
  //         result.push({ cmsService: cms, apiService: service });
  //       }
  //     }
  //   });

  //   return result;
  // })();

  return (
    <div
      id="step-service-type-select"
      className="w-full grid grid-cols-1 lg:grid-cols-2 items-stretch mt-4 gap-4"
    >
      {servicesToShow.length === 0 && !loading && (
        <div className="col-span-full bg-red-400 text-white p-4 mx-auto rounded-md">
          <AlertTriangle size={24} className="text-white mx-auto mb-2" />
          <span className="text-white text-center block">
            {t("Si è verificato un errore durante la ricerca dei servizi")}
          </span>
          <span className="text-white text-center block text-xs">
            {t("Contattaci per ottenere assistenza")}
          </span>
        </div>
      )}

      {servicesToShow.map((item, index) => {
        const { cmsService, apiService } = item;
        const variants: any[] = item.variants || [apiService];
        const isGroupedVariant = variants.length > 1;
        const isSelected =
          selectedValue?.id === cmsService?.id ||
          variants.some((v: any) => selectedValue?.Code === v.Code);

        const getCartQuantity = (serviceCodes: string[]) => {
          if (!currentBookingCart?.BasketItems) return 0;
          return currentBookingCart.BasketItems.filter(
            (item: any) =>
              item.Role === "INTEGRATION" &&
              serviceCodes.includes(item.Service),
          ).reduce(
            (sum: number, item: any) => sum + (item.Quantity?.Total || 0),
            0,
          );
        };

        const variantCodes = variants.map((v: any) => v.Code);
        const cartQuantity = getCartQuantity(variantCodes);
        const serviceKey =
          cmsService?.id || apiService?.Code || `service-${index}`;
        const serviceCodes =
          cmsService?.acf?.codes?.map((c: any) => c.code) || [];
        const applicableVouchersCount = getApplicableVouchersCount(
          serviceCodes,
          apiService?.Type,
        );

        const allAvailabilities = variants.flatMap(
          (v: any) =>
            (Array.isArray(v.Availability) ? v.Availability : []) as any[],
        );

        const minPrice = (() => {
          if (allAvailabilities.length === 0) return null;
          const prices: number[] = [];
          allAvailabilities.forEach((avail: any) => {
            const price = avail.EffectivePrice?.[0]?.Price || avail.Price;
            if (price !== null && price !== undefined) {
              prices.push(price);
            }
          });
          return prices.length > 0 ? Math.min(...prices) : null;
        })();

        // Price SHOWN on the card: per person for a package (a package is
        // never grouped with other codes, so `apiService` is its only
        // variant). `minPrice` stays as is for the availability checks.
        const displayedMinPrice =
          minPrice !== null ? getCardPrice(apiService, minPrice) : null;

        // Card "show_anyway": l'API esterna comanda. Il flag in ACF è solo
        // un fallback per quando l'API NON restituisce il code: in quel
        // caso `ServiceTypeStep` inietta un ghost con `isShowAnyway: true`.
        // Se l'API restituisce il code (servizio disponibile) la card si
        // comporta normalmente, anche se il CMS ha il flag attivo.
        const isShowAnyway = !!apiService?.isShowAnyway;
        const showAnywayText = apiService?.showAnywayText || "";

        const hasValidAvailability =
          !isShowAnyway &&
          allAvailabilities.length > 0 &&
          allAvailabilities.some((avail: any) => avail.Quantity?.Total > 0) &&
          minPrice !== null &&
          minPrice > 0;

        const codeImageId = isGroupedVariant
          ? null
          : cmsService?.acf?.codes?.find(
            (c: any) => c.code === apiService.Code,
          )?.image?.id;
        const displayImage = codeImageId || cmsService?.acf?.image?.id;

        const cardTitle = isGroupedVariant
          ? cmsService?.acf?.title ||
            cmsService?.name ||
            apiService.Name ||
            apiService.Code
          : cmsService?.acf?.codes?.find(
              (c: any) => c.code === apiService.Code,
            )?.short_description ||
            cmsService?.acf?.title ||
            apiService.Name ||
            apiService.Code;
        const { subtitle: cardSubtitle, description: cardDescription } =
          getServiceCardTexts(
            cmsService,
            isGroupedVariant ? null : apiService.Code,
          );

        return (
          <div
            key={serviceKey + index}
            className={cn(
              "w-full h-full rounded-2xl overflow-hidden relative shadow-md bg-white flex flex-col",
              {
                "ring-2 ring-secondary": isSelected,
                "cursor-pointer":
                  hasValidAvailability && !loading && !isShowAnyway,
                "cursor-not-allowed opacity-60":
                  (!hasValidAvailability && !isShowAnyway) || isShowAnyway,
                "opacity-50": loading,
              },
            )}
            onClick={() => {
              if (isShowAnyway) return;
              if (hasValidAvailability && !loading) {
                onSelect(cmsService || apiService, apiService);
              }
            }}
          >
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex flex-col items-center justify-center rounded-2xl">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
              </div>
            )}
            <ServiceInfoButton
              title={cardTitle}
              description={cardDescription}
              imageId={displayImage}
            />
            {displayImage && (
              <div className="w-full h-52 relative shrink-0">
                <motion.img
                  onUpdate={keepAnimationOnMainThread}
                  alt=""
                  src={`/cms-asset/${displayImage}`}
                  className={cn(
                    "w-full h-full object-cover object-center z-10",
                    {
                      "opacity-50": !hasValidAvailability,
                    },
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1 + 0.5,
                    ease: "easeInOut",
                  }}
                />
                <FeaturedBadge
                  cmsService={cmsService}
                  code={isGroupedVariant ? null : apiService.Code}
                  onImage
                  className="absolute bottom-0 -right-0.5 z-20"
                />
                {!hasValidAvailability && !isShowAnyway && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {t("SOLD OUT")}
                    </span>
                  </div>
                )}
                {isShowAnyway && showAnywayText && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-60 flex items-center justify-center p-4">
                    <span className="text-white font-semibold text-center text-base">
                      {showAnywayText}
                    </span>
                  </div>
                )}
                {cartQuantity > 0 && hasValidAvailability && (
                  <div className="absolute top-2 right-2 z-20 bg-muted text-secondary rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
                    {cartQuantity}
                  </div>
                )}
                {applicableVouchersCount > 0 && hasValidAvailability && (
                  <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-secondary text-white rounded-full px-2 py-1">
                    <Ticket size={14} />
                    <span className="text-xs font-semibold">
                      {applicableVouchersCount}
                    </span>
                  </div>
                )}
              </div>
            )}
            {!displayImage &&
              cartQuantity > 0 &&
              hasValidAvailability && (
                <div className="absolute top-2 right-2 z-20 bg-muted text-secondary rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
                  {cartQuantity}
                </div>
              )}
            {!displayImage &&
              applicableVouchersCount > 0 &&
              hasValidAvailability && (
                <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-secondary text-white rounded-full px-2 py-1">
                  <Ticket size={14} />
                  <span className="text-xs font-semibold">
                    {applicableVouchersCount}
                  </span>
                </div>
              )}
            <div className="p-4 flex flex-row items-center justify-between flex-1">
              <div
                className={cn("flex flex-col relative", {
                  "pl-10": !displayImage && !!cardDescription,
                })}
              >
                {!hasValidAvailability &&
                  !isShowAnyway &&
                  !cmsService?.acf?.image?.id && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-50 flex items-center justify-center rounded-b-2xl">
                    <span className="text-white font-bold text-lg">
                      {t("SOLD OUT")}
                    </span>
                  </div>
                )}
                {isShowAnyway && !displayImage && showAnywayText && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-60 flex items-center justify-center rounded-b-2xl p-3">
                    <span className="text-white font-semibold text-center text-sm">
                      {showAnywayText}
                    </span>
                  </div>
                )}
                <div className="flex flex-col space-y-1 min-h-16 justify-center">
                  {!displayImage && (
                    <FeaturedBadge
                      cmsService={cmsService}
                      code={isGroupedVariant ? null : apiService.Code}
                    />
                  )}
                  <span
                    className={cn(" text-lg leading-[1.2]  font-semibold", {
                      "opacity-50": !hasValidAvailability,
                    })}
                    dangerouslySetInnerHTML={{ __html: cardTitle }}
                  />
                  <ServiceCardSubtitle text={cardSubtitle} />
                  <ServicePriceLabel
                    cmsService={cmsService}
                    code={isGroupedVariant ? null : apiService.Code}
                    isPackage={apiService.Type === "Package"}
                    totalGuests={totalGuests}
                  />
                </div>
              </div>
              {displayedMinPrice !== null &&
                displayedMinPrice > 0 &&
                hasValidAvailability && (
                <div className="bg-secondary rounded-2xl z-20">
                  <span className="text-secondary-foreground block px-2 py-0.5 whitespace-nowrap">
                    {isGroupedVariant ? `${t("da")} ` : ""}€
                    {displayedMinPrice.toLocaleString(i18n.language, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ServiceTypeSelect;
